import { useState } from "react";
import {
  CheckCircle2,
  XCircle,
  Clock3,
  Coins,
  Search,
  Filter,
  AlertCircle,
  FileText,
  UserRound,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { openReceiptPdfWindow } from "@/lib/receipt-pdf";

interface AdvanceApprovalsTabProps {
  idToken: string | null;
}

export function AdvanceApprovalsTab({ idToken }: AdvanceApprovalsTabProps) {
  const [filterStatus, setFilterStatus] = useState<"all" | "pending" | "approved" | "rejected">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [decisionNotes, setDecisionNotes] = useState<Record<number, string>>({});

  const listQuery = trpc.salaryAdvance.adminList.useQuery(
    { idToken: idToken || "" },
    { enabled: Boolean(idToken) }
  );

  const decideMutation = trpc.salaryAdvance.decide.useMutation({
    onSuccess: (_, variables) => {
      toast.success(
        variables.status === "approved"
          ? "อนุมัติคำขอเบิกเงินล่วงหน้าเรียบร้อยแล้ว"
          : "บันทึกไม่อนุมัติคำขอเรียบร้อยแล้ว"
      );
      listQuery.refetch();
    },
    onError: (err) => {
      toast.error(err.message || "เกิดข้อผิดพลาดในการบันทึกคำขอ");
    },
  });

  const handleDecide = (id: number, status: "approved" | "rejected") => {
    if (!idToken) {
      toast.error("กรุณาเข้าสู่ระบบก่อนทำรายการ");
      return;
    }
    const note = decisionNotes[id]?.trim() || "";
    decideMutation.mutate({
      idToken,
      id,
      status,
      note,
    });
  };

  const requests = listQuery.data?.requests ?? [];
  const currentPeriod = listQuery.data?.currentPeriod;

  // Filter requests
  const filteredRequests = requests.filter((r: any) => {
    const matchStatus = filterStatus === "all" || r.status === filterStatus;
    const matchSearch =
      !searchQuery ||
      r.displayName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.reason?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchStatus && matchSearch;
  });

  // KPI counts
  const pendingCount = requests.filter((r: any) => r.status === "pending").length;
  const approvedCount = requests.filter((r: any) => r.status === "approved").length;
  const totalApprovedAmount = requests
    .filter((r: any) => r.status === "approved")
    .reduce((sum: number, r: any) => sum + (Number(r.amount) || 0), 0);

  return (
    <div className="space-y-4">
      {/* Header & KPI Card */}
      <Card className="glass-panel overflow-hidden rounded-3xl border border-slate-200 shadow-lg">
        <div className="h-1.5 bg-gradient-to-r from-[#0b132b] via-[#152238] to-[#f59e0b]" />
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="flex h-2.5 w-2.5 rounded-full bg-amber-500 animate-pulse" />
                <span className="text-xs font-black uppercase tracking-wider text-amber-800">
                  ฝ่ายบริหาร / HR Management
                </span>
              </div>
              <CardTitle className="text-xl sm:text-2xl font-black text-slate-900">
                อนุมัติคำขอเบิกเงินล่วงหน้า
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm text-slate-500 font-medium">
                รอบเงินเดือนปัจจุบัน:{" "}
                <span className="font-bold text-amber-700">
                  {currentPeriod?.label || "ทุกวันที่ 10 และ 25 ของเดือน"}
                </span>{" "}
                ({currentPeriod?.startDate} ถึง {currentPeriod?.endDate})
              </CardDescription>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => listQuery.refetch()}
              className="rounded-full border-amber-200 text-amber-950 hover:bg-amber-50 text-xs font-bold"
            >
              <RefreshCw size={13} className={`mr-1.5 ${listQuery.isFetching ? "animate-spin" : ""}`} />
              รีเฟรชข้อมูล
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          {/* KPI Mini-cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
            <div className="rounded-2xl bg-amber-50/70 border border-amber-200/80 p-3.5 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center font-black">
                <Clock3 size={20} />
              </div>
              <div>
                <p className="text-[11px] font-bold text-amber-800">รอการอนุมัติ</p>
                <p className="text-xl font-black text-amber-950 font-numeric">
                  {pendingCount} <span className="text-xs font-semibold">รายการ</span>
                </p>
              </div>
            </div>

            <div className="rounded-2xl bg-amber-50/90 border border-amber-200/90 p-3.5 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-amber-100 text-amber-900 flex items-center justify-center font-black">
                <CheckCircle2 size={20} />
              </div>
              <div>
                <p className="text-[11px] font-bold text-amber-800">อนุมัติแล้ว</p>
                <p className="text-xl font-black text-amber-950 font-numeric">
                  {approvedCount} <span className="text-xs font-semibold">รายการ</span>
                </p>
              </div>
            </div>

            <div className="rounded-2xl bg-slate-50 border border-slate-200 p-3.5 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-black">
                <Coins size={20} />
              </div>
              <div>
                <p className="text-[11px] font-bold text-slate-600">ยอดเบิกที่อนุมัติรวม</p>
                <p className="text-xl font-black text-slate-900 font-numeric">
                  ฿{totalApprovedAmount.toLocaleString()}{" "}
                  <span className="text-xs font-semibold text-slate-500">บาท</span>
                </p>
              </div>
            </div>
          </div>

          {/* Search & Filter Bar */}
          <div className="flex flex-col sm:flex-row gap-2.5 pt-2">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="ค้นหาชื่อพนักงาน หรือเหตุผล..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 rounded-2xl border-slate-200 text-xs sm:text-sm bg-white"
              />
            </div>

            <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
              {(
                [
                  { id: "all", label: "ทั้งหมด" },
                  { id: "pending", label: "รออนุมัติ" },
                  { id: "approved", label: "อนุมัติแล้ว" },
                  { id: "rejected", label: "ไม่อนุมัติ" },
                ] as const
              ).map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setFilterStatus(tab.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition whitespace-nowrap ${
                    filterStatus === tab.id
                      ? "bg-[#0b132b] text-white shadow-xs"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200/70"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Requests List */}
      <div className="space-y-3">
        {listQuery.isLoading ? (
          <div className="p-8 text-center glass-panel rounded-3xl border border-slate-200">
            <RefreshCw className="animate-spin mx-auto text-amber-500 mb-2" size={24} />
            <p className="text-sm font-bold text-slate-600">กำลังโหลดรายการขอเบิกเงิน...</p>
          </div>
        ) : listQuery.error ? (
          <div className="p-8 text-center glass-panel rounded-3xl border border-rose-200 bg-rose-50/90">
            <AlertCircle className="mx-auto text-rose-500 mb-2" size={32} />
            <p className="text-sm font-black text-rose-800">โหลดรายการอนุมัติไม่สำเร็จ</p>
            <p className="mt-1 text-xs text-rose-600">{listQuery.error.message}</p>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="p-8 text-center glass-panel rounded-3xl border border-slate-200">
            <AlertCircle className="mx-auto text-slate-400 mb-2" size={32} />
            <p className="text-sm font-bold text-slate-700">ไม่พบรายการขอเบิกเงินล่วงหน้า</p>
            <p className="text-xs text-slate-400 mt-1">
              {filterStatus !== "all"
                ? `ไม่มีรายการในสถานะ "${filterStatus}"`
                : "ยังไม่มีพนักงานส่งคำขอเบิกเงินในระบบ"}
            </p>
          </div>
        ) : (
          filteredRequests.map((r: any) => (
            <Card
              key={r.id}
              className="glass-panel overflow-hidden rounded-3xl border border-[#e5ede7] shadow-sm hover:shadow-md transition"
            >
              <CardContent className="p-4 sm:p-5">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Employee Info & Request details */}
                  <div className="space-y-2 flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-900 border border-amber-200 font-bold">
                        <UserRound size={18} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-black text-base text-slate-900 truncate">
                          {r.displayName}
                        </h3>
                        <p className="text-[11px] font-semibold text-slate-400">
                          ยื่นคำขอเมื่อ:{" "}
                          {new Date(r.createdAt).toLocaleString("th-TH", {
                            timeZone: "Asia/Bangkok",
                            dateStyle: "medium",
                            timeStyle: "short",
                          })}
                        </p>
                      </div>

                      {/* Status Badge */}
                      <div className="ml-auto lg:ml-0">
                        {r.status === "pending" && (
                          <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100 rounded-full text-xs font-bold border border-amber-300">
                            <Clock3 size={12} className="mr-1 text-amber-700" /> รออนุมัติ
                          </Badge>
                        )}
                        {r.status === "approved" && (
                          <div className="flex items-center gap-2">
                            <Badge className="bg-amber-50 text-amber-950 hover:bg-amber-50 rounded-full text-xs font-bold border border-amber-200">
                              <CheckCircle2 size={12} className="mr-1 text-amber-600" /> อนุมัติแล้ว
                            </Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openReceiptPdfWindow({
                                receiptNo: `ADV-${r.periodLabel.replace(/[^0-9]/g, "").slice(0, 6) || "2026"}-${String(r.id).padStart(4, "0")}`,
                                employeeName: r.displayName,
                                amount: Number(r.amount),
                                periodLabel: r.periodLabel,
                                workedDays: r.workedDaysAtRequest,
                                approvedAt: r.decidedAt || r.createdAt,
                                approverName: r.approverName || "ฝ่ายบริหาร (HR)",
                                reason: r.reason,
                              })}
                              className="h-7 px-3 rounded-full text-xs font-bold border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-100"
                            >
                              <FileText size={12} className="mr-1" /> พิมพ์ใบเสร็จ PDF
                            </Button>
                          </div>
                        )}
                        {r.status === "rejected" && (
                          <Badge className="bg-rose-100 text-rose-900 hover:bg-rose-100 rounded-full text-xs font-bold border border-rose-300">
                            <XCircle size={12} className="mr-1 text-rose-700" /> ไม่อนุมัติ
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Eligibility details card */}
                    <div className="rounded-2xl bg-slate-50/90 border border-slate-200/80 p-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs">
                      <div>
                        <span className="text-[10px] text-slate-400 font-semibold block">รอบเงินเดือน</span>
                        <span className="font-bold text-slate-700 truncate block">{r.periodLabel}</span>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 font-semibold block">วันทำงานขณะยื่น</span>
                        <span className="font-bold text-slate-800 font-numeric">{r.workedDaysAtRequest} วัน</span>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 font-semibold block">สิทธิ์เบิกสูงสุด</span>
                        <span className="font-bold text-emerald-800 font-numeric">
                          ฿{Number(r.maxAllowedAmount).toLocaleString()}
                        </span>
                      </div>
                      <div>
                        <span className="text-[10px] text-slate-400 font-semibold block">ยอดที่ขอเบิก</span>
                        <span className="font-black text-amber-800 text-sm font-numeric">
                          ฿{Number(r.amount).toLocaleString()}
                        </span>
                      </div>
                    </div>

                    {/* Reason */}
                    {r.reason && (
                      <p className="text-xs text-slate-600 bg-white/80 rounded-xl p-2 border border-slate-200/60">
                        <span className="font-bold text-slate-700">เหตุผล: </span>
                        {r.reason}
                      </p>
                    )}

                    {/* Approver info if already decided */}
                    {r.status !== "pending" && (
                      <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-2 pt-0.5">
                        <span>
                          ดำเนินการโดย: <strong className="text-slate-700">{r.approverName || "ฝ่ายบริหาร"}</strong>
                        </span>
                        {r.decisionNote && (
                          <span>
                            · หมายเหตุ: <span className="italic text-slate-600">"{r.decisionNote}"</span>
                          </span>
                        )}
                        {r.decidedAt && (
                          <span>
                            · วันที่:{" "}
                            {new Date(r.decidedAt).toLocaleString("th-TH", {
                              timeZone: "Asia/Bangkok",
                              dateStyle: "short",
                              timeStyle: "short",
                            })}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions for Pending */}
                  {r.status === "pending" && (
                    <div className="flex flex-col gap-2 shrink-0 lg:w-64 pt-2 lg:pt-0 border-t lg:border-t-0 lg:border-l border-slate-200/80 lg:pl-4">
                      <Input
                        placeholder="ระบุหมายเหตุ (ถ้ามี)..."
                        value={decisionNotes[r.id] ?? ""}
                        onChange={(e) =>
                          setDecisionNotes((prev) => ({ ...prev, [r.id]: e.target.value }))
                        }
                        className="text-xs h-9 rounded-xl border-slate-200 bg-white"
                      />

                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          className="btn-primary-teal h-9 text-xs font-black rounded-xl shadow-xs"
                          disabled={decideMutation.isPending}
                          onClick={() => handleDecide(r.id, "approved")}
                        >
                          <CheckCircle2 size={14} className="mr-1" />
                          อนุมัติ
                        </Button>

                        <Button
                          variant="outline"
                          className="h-9 text-xs font-bold rounded-xl border-rose-200 bg-rose-50/70 text-rose-700 hover:bg-rose-100 hover:text-rose-800"
                          disabled={decideMutation.isPending}
                          onClick={() => handleDecide(r.id, "rejected")}
                        >
                          <XCircle size={14} className="mr-1" />
                          ไม่อนุมัติ
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
